/** Thin REST client. The token lives in sessionStorage so a closed tab signs out. */
const BASE = '/api/v1';
const KEY = 'nova.token';

export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
  }
}

export function getToken(): string | null {
  try {
    return sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string | null): void {
  try {
    if (token) sessionStorage.setItem(KEY, token);
    else sessionStorage.removeItem(KEY);
  } catch {
    /* storage unavailable: the session lasts as long as the page */
  }
}

let onUnauthorized: (() => void) | undefined;
export function whenUnauthorized(fn: () => void): void {
  onUnauthorized = fn;
}

/** Sends `body` as JSON, or as-is with its own type when it is a Blob (file uploads). */
export async function api<T = unknown>(method: string, path: string, body?: unknown): Promise<T> {
  const token = getToken();
  const blob = body instanceof Blob ? body : null;
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers: {
        ...(blob ? { 'Content-Type': blob.type } : body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: blob ?? (body !== undefined ? JSON.stringify(body) : undefined),
    });
  } catch {
    throw new ApiError(0, 'Nova is unreachable. Check your connection.');
  }
  if (res.status === 401 && path !== '/auth/login') onUnauthorized?.();
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const data = await res.json();
      message = Array.isArray(data.message) ? data.message.join(', ') : data.message ?? message;
    } catch {
      /* not JSON */
    }
    throw new ApiError(res.status, message);
  }
  // 204, or an empty 200 (a handler that returned nothing)
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/** A file from Nova, fetched with the session (files have no public links). */
export async function apiBlob(path: string): Promise<Blob> {
  const token = getToken();
  const res = await fetch(`${BASE}${path}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) throw new ApiError(res.status, res.status === 410 ? 'This file was deleted' : `Could not open the file (${res.status})`);
  return res.blob();
}

/** Uploads a file as raw bytes; its name goes in the query. */
export function upload<T>(path: string, file: File): Promise<T> {
  const sep = path.includes('?') ? '&' : '?';
  return api<T>('POST', `${path}${sep}name=${encodeURIComponent(file.name)}`, new Blob([file], { type: 'application/octet-stream' }));
}
